import { parseDate } from "helpers/date";
import { centsToDollars, parsePrice } from "helpers/price";
import { useMemo } from "react";
import { IPayment } from "types/Payment";

/**
 * CLE-2798 — credit-card fees collected on a payment plan, one line per PROCESSED charge (deposit,
 * installment, pay-remaining), with the clear fee that charge waived in return (Blake's rule). Nothing
 * is shown before a charge runs: the fee only exists once Finix has told us the card was CREDIT, and it
 * is never part of the plan schedule or remaining balance.
 */
export function CreditCardFeesCollected({
  payments,
}: {
  payments: IPayment[];
}) {
  const rows = useMemo(
    () =>
      (payments || [])
        .filter(
          (p) =>
            p.status === "Paid" && (p.gfeCreditCardFeeAmountInCents || 0) > 0
        )
        .sort(
          (a, b) =>
            new Date(a.createdAt || 0).getTime() -
            new Date(b.createdAt || 0).getTime()
        ),
    [payments]
  );

  if (rows.length === 0) return null;

  const total = rows.reduce(
    (sum, p) => sum + (p.gfeCreditCardFeeAmountInCents || 0),
    0
  );

  return (
    <div
      className="flex flex-column gap-1 mt-3 p-3 border-1 border-round surface-border"
      data-testid="credit-card-fees-collected"
    >
      <p className="font-semibold text-sm mb-1">
        Surcharge (credit card, additional charge, not part of the estimate)
      </p>
      {rows.map((p) => (
        <div key={p.id} className="flex justify-content-between text-sm">
          <span>
            {parseDate(p.createdAt as any, false, true)} — payment of{" "}
            {parsePrice(centsToDollars(p.amountInCents || 0))}
          </span>
          <span>
            + {parsePrice(centsToDollars(p.gfeCreditCardFeeAmountInCents || 0))}
            {(p.clearFeeReducedByCreditCardFeeInCents || 0) > 0 && (
              <span className="text-gray-600">
                {" "}
                (clear fee waived{" "}
                {parsePrice(
                  centsToDollars(p.clearFeeReducedByCreditCardFeeInCents || 0)
                )}
                )
              </span>
            )}
          </span>
        </div>
      ))}
      {rows.length > 1 && (
        <div className="flex justify-content-between text-sm font-semibold mt-1">
          <span>Total surcharge</span>
          <span>{parsePrice(centsToDollars(total))}</span>
        </div>
      )}
    </div>
  );
}
